import { format } from 'date-fns'

const helperMixin = {
  methods: {
    getOptions: options => options.map(o => ({ text: o.value, value: o.id })),
    getFlatOptions: options => options.map(o => ({ text: o, value: o })),
    getObjectOptions: options => {
      const keys = Object.keys(options)
      return keys.map(key => ({ text: key, value: options[key] }))
    },
    getObjectText: (options, id) => options.find(o => o.value === id)?.text,
    getObjectOptionsText: (options, id) => {
      const keys = Object.keys(options)
      const opt = keys.map(key => ({ text: key, value: options[key] }))
      return opt.find(o => o.value === id)?.text
    },
    calculateDataRatio(totalTraffic, trafficSize) {
      if (totalTraffic > 0 && trafficSize > 0) {
        const dataRatio = (trafficSize / totalTraffic) * 100
        return `${dataRatio.toFixed(2)}%`
      }
      return '0.00%'
    },
    updateStateWithValues(state, value, responseKeys) {
      if (value.total > 0) {
        responseKeys.forEach(responseKey => {
          const totalValue = value.result[responseKeys.indexOf(responseKey)].total

          state[responseKey].percentValue = this.calculateDataRatio(value.total, totalValue)
          state[responseKey].number = totalValue
        })
      } else {
        responseKeys.forEach(responseKey => {
          state[responseKey].percentValue = 0
          state[responseKey].number = 0
        })
      }
    },
    unixTimestampToDateTime(unixTimestamp) {
      if (unixTimestamp === null || unixTimestamp === undefined) {
        return 'No timestamp available'
      }

      const date = new Date(unixTimestamp * 1000).toISOString()

      const options = {
        year: 'numeric',
        month: 'numeric',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        timeZone: 'UTC',
      }

      const formattedDate = date.toLocaleString('en-GB', options)
      return this.formatOffsetDateTime(formattedDate)
    },
    formatNumberWithUnit(number) {
      if (number >= 1e9) {
        return `${(number / 1e9).toFixed(2)}B`
      } if (number >= 1e6) {
        return `${(number / 1e6).toFixed(2)}M`
      } if (number >= 1e3) {
        return `${(number / 1e3).toFixed(2)}K`
      }
      return number ? number.toString() : '0'
    },
    isNullOrZero: value => value === 0 || value === '0' || value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && !Number.isNaN(value)),
    isNullOrEmpty: value => value === null || value === undefined || (typeof value === 'string' && value.trim() === '') || (typeof value === 'number' && !Number.isNaN(value)),
    getText: (options, id) => options.find(o => o.id === id)?.value,
    formatDateTime: isoDate => (isoDate ? format(new Date(`${isoDate}Z`), 'yyyy-MM-dd HH:mm:ss') : '-'),
    formatOffsetDateTime: isoDate => (isoDate ? format(new Date(`${isoDate}`), 'yyyy-MM-dd HH:mm:ss') : '-'),
    formatDateTimeDetailed: isoDate => (isoDate ? format(new Date(`${isoDate}Z`), 'yyyy-MM-dd HH:mm:ss') : '-'),
    formatOffsetDateTimeDetailed: isoDate => (isoDate ? format(new Date(`${isoDate}`), 'yyyy-MM-dd HH:mm:ss') : '-'),
    formatBytes(num) {
      if (typeof num !== 'number' || Number.isNaN(num)) {
        return '-'
      }

      const neg = num < 0
      const units = ['B', 'KB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']

      if (neg) {
        num = -num
      }

      if (num < 1) {
        return `${(neg ? '-' : '')}${num} B`
      }

      const exponent = Math.min(Math.floor(Math.log10(num) / Math.log10(1000)), units.length - 1)
      // eslint-disable-next-line no-restricted-properties
      const numFormatted = (num / Math.pow(1000, exponent)).toFixed(2)
      const unit = units[exponent]

      return `${(neg ? '-' : '')}${numFormatted}${unit}`
    },
    formatFixedNumbers(num) {
      const intPart = Math.trunc(num)
      const floatPart = Number((num - intPart))
      if (floatPart > 0.09) {
        return num.toFixed(2)
      }
      if (floatPart > 0.009) {
        return num.toFixed(3)
      }
      if (floatPart > 0.0009) {
        return num.toFixed(4)
      }
      return num.toFixed(0)
    },
    errorToast(message, title = 'Error') {
      this.generalToast(message, title, 'danger')
    },
    successToast(message, title = 'Success') {
      this.generalToast(message, title, 'success')
    },
    infoToast(message, title = 'Info') {
      this.generalToast(message, title, 'primary')
    },
    generalToast(message, title, variant) {
      this.$bvToast.toast(message, {
        toastClass: 'general-toast-class',
        solid: true,
        title,
        variant,
      })
    },
    confirmDelete(
      title = 'Delete',
      message = 'Are you sure you want to delete?',
      okTitle = 'Delete',
      cancelTitle = 'Cancel',
    ) {
      return this.$bvModal
        .msgBoxConfirm(message, {
          title,
          okVariant: 'danger',
          okTitle,
          cancelTitle,
          cancelVariant: 'bg-white',
          hideHeaderClose: false,
          headerClass: 'text-break',
          centered: true,
        })
        .catch()
    },
    copySuccess() {
      this.infoToast('Copied to clipboard', 'Clipboard')
    },
    timeSince(dateAccepted, isUnix) {
      if (dateAccepted === null || dateAccepted === undefined) return null
      if (isUnix) {
        dateAccepted = new Date(dateAccepted * 1000).toISOString()
      } else if (!dateAccepted.endsWith('Z')) {
        dateAccepted += 'Z'
      }

      const currentDate = new Date()
      const acceptedDate = new Date(dateAccepted)
      const elapsedMilliseconds = currentDate - acceptedDate
      const elapsedSeconds = Math.floor(elapsedMilliseconds / 1000)

      const timeUnits = [
        { unit: 'year', seconds: 31536000 },
        { unit: 'month', seconds: 2592000 },
        { unit: 'day', seconds: 86400 },
        { unit: 'hour', seconds: 3600 },
        { unit: 'min', seconds: 60 },
      ]

      const result = timeUnits.find(({ seconds }) => elapsedSeconds >= seconds)

      if (result) {
        const { unit, seconds } = result
        const interval = Math.floor(elapsedSeconds / seconds)
        return `${interval} ${unit}${interval !== 1 ? 's' : ''}`
      }

      return `${elapsedSeconds} sec`
    },
    formatMilliseconds(milliseconds) {
      const ms = milliseconds

      let minutes = ms / 60000

      if (minutes / 1440 >= 1) {
        return `${this.formatToInt((minutes / 1440).toFixed(1))} day${
          Math.round(minutes / 1440) !== 1 ? 's' : ''
        }`
      }
      if (minutes / 60 >= 1) {
        return `${this.formatToInt((minutes / 60).toFixed(1))} hour${
          Math.round(minutes / 60) !== 1 ? 's' : ''
        }`
      }
      if (minutes >= 1) {
        return `${this.formatToInt(minutes.toFixed(1))} minute${
          Math.round(minutes) !== 1 ? 's' : ''
        }`
      }
      if (minutes >= 0.016) {
        minutes *= 60
        return `${this.formatToInt(minutes.toFixed(1))} second${
          Math.round(minutes) !== 1 ? 's' : ''
        }`
      }
      minutes *= 60000
      return `${this.formatToInt(ms)} millisecond${
        Math.round(ms) !== 1 ? 's' : ''
      }`
    },
    formatToInt(number) {
      if (number.toString().endsWith('.0')) {
        return parseInt(number, 10)
      }
      return number
    },
    calculateMedian(numbers) {
      if (numbers.length === 0) {
        return '-'
      }
      const sorted = numbers.slice().sort((a, b) => a - b)
      const middle = Math.floor(sorted.length / 2)

      return sorted.length % 2 === 0
        ? ((sorted[middle - 1] + sorted[middle]) / 2).toFixed(0)
        : sorted[middle].toFixed(0)
    },
    calculateAverage(numbers, fractionDigits) {
      if (numbers.length === 0) {
        return '-'
      }
      const avg = numbers.reduce((p, c) => p + c, 0) / numbers.length
      return avg.toFixed(fractionDigits)
    },
    formatCamelCaseString(text) {
      return text
        .replace(/(_|-)/g, ' ')
        .trim()
        .replace(/\w\S*/g, str => text.charAt(0).toUpperCase() + str.substr(1))
        .replace(/([a-z])([A-Z])/g, '$1 $2')
        .replace(/([A-Z])([A-Z][a-z])/g, '$1 $2')
    },
    wizardToTop() {
      const el = this.$refs.gjWizard
      if (el) el.scroll({ top: 0, behavior: 'smooth' })
    },
    getFileNameFromContentDisposition(contentDisposition) {
      const regex = /filename[^;=\n]*=((['"]).*?\2|[^;\n]*)/
      const matches = contentDisposition.match(regex)
      if (matches && matches.length > 1) {
        const encodedFileName = matches[1]
        // Check if the file name is URL-encoded
        if (encodedFileName.startsWith("UTF-8''")) {
          // Decode the file name
          return decodeURIComponent(encodedFileName.substring(7))
        }
        return encodedFileName
      }
      return null // Return null if no file name is found
    },
    // eslint-disable-next-line eqeqeq
    // hasPermission: (permissions, permission) => permissions.some(p => p.type == permission),
  },
}

export default helperMixin
